import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DesktopConfig, OneClickConfig, OneClickFile, ScreenBox } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { WindowTitlebar } from "../WindowTitlebar";
import { MfitConsent } from "../mfit/MfitConsent";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { useSim } from "../../state/store";
import type { MfitDone, MfitGuideFn } from "../mfit/shared";
import "./oneclick.css";

/**
 * "One Click Too Many" — Shadow IT / Shadow AI / vibe coding / malicious OAuth.
 * Rendered inside the reused Windows Desktop on the booth laptop (office monitor
 * box). Jamie, a benign colleague, nudges from a portrait card; the attacks are
 * the AI dashboard upload (QuickForge) and the OAuth consent (ExpoSync).
 * The device never uploads or connects anything; "doing it" is simulated.
 * Reports {key, events[], evidence} up through App's mfitDone.
 */

const DESK_APPS = [
  { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  { id: "outlook", label: "Mail", icon: "outlook", opens: "outlook" },
  { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
  { id: "files", label: "File Explorer", icon: "files", opens: "files" },
];

function Chrome({ url, children, onReload }: { url: string; children: ReactNode; onReload?: () => void }) {
  return (
    <div className="ocx-win">
      <WindowTitlebar title="Chrome" />
      <div className="ocx-addr">
        <span className="ocx-addr-nav" aria-hidden="true">{"\u2039 \u203A"}</span>
        <button type="button" className="ocx-addr-reload" aria-label="Reload" onClick={onReload} disabled={!onReload}>{"\u21BB"}</button>
        <span className="ocx-addr-url"><i aria-hidden="true">{"\u{1F512}"}</i>{url}</span>
      </div>
      <div className="ocx-page">{children}</div>
    </div>
  );
}

/** Jamie's portrait + message card, pinned inside the screen. */
function JamieCard({ jamie, text }: { jamie?: OneClickConfig["jamie"]; text?: string }) {
  if (!jamie || !text) return null;
  return (
    <div className="ocx-jamie" role="note">
      {jamie.avatarUrl ? <img className="ocx-jamie-av" src={jamie.avatarUrl} alt="" /> : <span className="ocx-jamie-av is-ph" aria-hidden="true">{jamie.name.slice(0, 1)}</span>}
      <div className="ocx-jamie-c">
        <b>{jamie.name}{jamie.role ? <small> · {jamie.role}</small> : null}</b>
        <span>{text}</span>
      </div>
    </div>
  );
}

export function OneClick({
  config,
  onDone,
  onGuideChange,
  onRecord,
  screenBox,
}: {
  config: OneClickConfig;
  onDone: (d: MfitDone) => void;
  onGuideChange?: MfitGuideFn;
  onRecord?: (eventId: string, evidence?: string) => void;
  screenBox?: ScreenBox | null;
}) {
  const doneRef = useRef(false);
  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };
  const rec = (eventId?: string, evidence?: string) => {
    if (eventId) onRecord?.(eventId, evidence);
  };
  const voice = (id?: string) => ({ voice: id ?? config.jamie?.voiceId ?? "coral", lang: activeTtsLang() });

  // Jamie's lines are voiced; prefetch them all so the prerender records them.
  useEffect(() => {
    Object.values(config.lines ?? {}).forEach((l) => void audio.prefetchSpeech(l, voice()));
    if (config.film?.outroLine) void audio.prefetchSpeech(config.film.outroLine, voice(config.film.outroVoiceId ?? "nova"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deskConfig: DesktopConfig = { clock: config.clock, date: config.date, icons: DESK_APPS, taskbar: DESK_APPS };

  if (config.mode === "film") return <FilmStage config={config} finish={finish} onGuideChange={onGuideChange} voice={voice} />;
  if (config.mode === "analytics") return <AnalyticsStage config={config} deskConfig={deskConfig} finish={finish} onGuideChange={onGuideChange} speak={(l, id) => void audio.speak(l, voice(id))} />;
  if (config.mode === "quickforge") return <QuickForgeStage config={config} deskConfig={deskConfig} finish={finish} rec={rec} onGuideChange={onGuideChange} speak={(l, id) => void audio.speak(l, voice(id))} />;
  if (config.mode === "sign") return <SignStage config={config} finish={finish} onGuideChange={onGuideChange} speak={(l, id) => void audio.speak(l, voice(id))} screenBox={screenBox} />;
  if (config.mode === "exposync") return <ExpoSyncStage config={config} deskConfig={deskConfig} finish={finish} rec={rec} onGuideChange={onGuideChange} speak={(l, id) => void audio.speak(l, voice(id))} />;
  if (config.mode === "summary") return <SummaryStage config={config} finish={finish} />;
  return null;
}

// ---------------------------------------------------------------- film
function FilmStage({ config, finish, onGuideChange, voice }: { config: OneClickConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn; voice: (id?: string) => { voice: string; lang: string } }) {
  const f = config.film!;
  // idle = cover with Watch/Skip; playing = video; outro = narrator voice plays,
  // Skip is gone and Watch is greyed with a "Speaking..." box until it finishes.
  const [phase, setPhase] = useState<"idle" | "playing" | "outro">("idle");
  const outroRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    onGuideChange?.(config.guide?.[phase === "playing" ? "film_playing" : phase === "outro" ? "film_done" : "film_intro"] ?? null);
    return () => onGuideChange?.(null);
  }, [phase, config.guide, onGuideChange]);
  useEffect(() => () => { if (videoRef.current) audio.detachFilm(videoRef.current); }, []);

  const done = (watched: boolean) => {
    if (outroRef.current) return;
    outroRef.current = true;
    setPhase("outro");
    const go = () => finish({ key: watched ? "watched" : "skipped", eventId: watched ? config.events?.watched : config.events?.skipped, evidence: watched ? "Watched the awareness film" : "Skipped the awareness film" });
    if (f.outroLine) void audio.speak(f.outroLine, voice(f.outroVoiceId ?? "nova")).then(go);
    else go();
  };

  return (
    <div className="ocx-film-stage">
      <div className={`ocx-film${phase === "playing" ? " is-playing" : ""}`}>
        {phase === "playing" ? (
          <>
            <video ref={videoRef} src={f.src} poster={f.poster} controls autoPlay playsInline onPlay={() => videoRef.current && audio.attachFilm(videoRef.current, 6)} onEnded={() => done(true)} />
            <button type="button" className="ocx-btn ocx-film-skip" onClick={() => done(false)}>
              {f.skipLabel ?? "Skip"}
            </button>
          </>
        ) : (
          <div className="ocx-film-cover" style={f.poster ? { backgroundImage: `url(${f.poster})` } : undefined}>
            <div className="ocx-film-card">
              {f.kicker && <span className="ocx-film-kicker">{f.kicker}</span>}
              <h2>{f.title}</h2>
              <p>{f.intro}</p>
              <div className="ocx-film-btns">
                <button
                  type="button"
                  className={`ocx-btn ocx-btn--primary${phase === "idle" ? " cf-pulse-target" : ""}`}
                  disabled={phase === "outro"}
                  title={phase === "outro" ? "Please wait for the narrator to finish." : undefined}
                  onClick={() => setPhase("playing")}
                >{f.watchLabel ?? "Watch the video"}</button>
                {phase === "idle" ? (
                  <button type="button" className="ocx-btn" onClick={() => done(false)}>{f.skipLabel ?? "Skip"}</button>
                ) : (
                  <span className="ocx-voicebox" role="status" aria-label="Voice speaking, please wait">
                    <span className="ocx-voicebars" aria-hidden="true"><i /><i /><i /><i /></span>
                    <span>{"Speaking\u2026"}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- analytics outage
function AnalyticsStage({ config, deskConfig, finish, onGuideChange, speak }: { config: OneClickConfig; deskConfig: DesktopConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn; speak: (l: string, id?: string) => void }) {
  const a = config.analytics!;
  const [stage, setStage] = useState<"desktop" | "portal" | "retry" | "message">("desktop");
  const spoke = useRef(false);
  useEffect(() => {
    onGuideChange?.(config.guide?.[`analytics_${stage}`] ?? null);
    if (stage === "message" && !spoke.current) {
      spoke.current = true;
      if (config.lines?.outage) speak(config.lines.outage);
    }
    return () => onGuideChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  return (
    <Desktop config={deskConfig} className="ocx-desk" highlightAppId={stage === "desktop" ? "browser" : undefined} openAppIds={stage === "desktop" ? [] : ["browser"]} activeAppId={stage === "desktop" ? null : "browser"} onOpen={(id) => id === "browser" && stage === "desktop" && setStage("portal")}>
      {stage !== "desktop" && (
        <Chrome url={a.url}>
          <div className="ocx-analytics">
            <header className="ocx-an-h"><b>{a.appName}</b><span>Q4 overview</span></header>
            <div className="ocx-an-err">
              <span className="ocx-an-err-ico" aria-hidden="true">{"\u26A0"}</span>
              <b>{a.error}</b>
              <p>{a.errorDetail}</p>
              {stage === "portal" && <button type="button" className="ocx-btn cf-pulse-target" onClick={() => { audio.ui("error"); setStage("retry"); }}>Try again</button>}
              {stage === "retry" && <button type="button" className="ocx-btn" disabled>Still unavailable…</button>}
            </div>
          </div>
        </Chrome>
      )}
      {stage === "retry" && (
        <button type="button" className="win-toast blink ocx-toast" onClick={() => setStage("message")}>
          <span className="win-toast-ico" aria-hidden="true">{"\u{1F4F9}"}</span>
          <span className="win-toast-text"><strong>Teams · Jamie Lee</strong><span>The reporting service is down…</span></span>
        </button>
      )}
      {stage === "message" && (
        <div className="ocx-teams">
          <header className="ocx-teams-h">
            {config.jamie?.avatarUrl ? <img className="ocx-teams-av" src={config.jamie.avatarUrl} alt="" /> : <span className="ocx-teams-av" aria-hidden="true">JL</span>}
            <div><b>{config.jamie?.name ?? "Jamie Lee"}</b><small>{config.jamie?.role ?? "Product specialist"}</small></div>
            <span className="ocx-teams-time">{a.jamieTime}</span>
          </header>
          <div className="ocx-teams-msg">{a.jamieMessage}</div>
          <div className="ocx-teams-link"><span className="ocx-teams-link-ico" aria-hidden="true">{"\u{1F517}"}</span><span><b>Demo dashboard</b><small>{a.link}</small></span></div>
          <button type="button" className="ocx-btn ocx-btn--primary cf-pulse-target" onClick={() => finish({ key: "open", eventId: config.events?.opened_link, evidence: "Opened Jamie's AI dashboard link" })}>{a.openLabel ?? "Open the demo dashboard"}</button>
        </div>
      )}
    </Desktop>
  );
}

// ---------------------------------------------------------------- QuickForge
function QuickForgeStage({ config, deskConfig, finish, rec, onGuideChange, speak }: { config: OneClickConfig; deskConfig: DesktopConfig; finish: (d: MfitDone) => void; rec: (id?: string, ev?: string) => void; onGuideChange?: MfitGuideFn; speak: (l: string, id?: string) => void }) {
  const q = config.quickforge!;
  type S = "gen" | "dashboard" | "picker" | "details" | "info" | "deploy" | "confirm";
  const [stage, setStage] = useState<S>("gen");
  // replay (what would have happened): only the confidential file + Public are offered
  const files = config.replay ? q.files.filter((f) => f.id === "confidential") : q.files;
  const [picked, setPicked] = useState<OneClickFile | null>(null);
  const [deploy, setDeploy] = useState<"public" | "restricted" | null>(null);
  const spoke = useRef<Record<string, boolean>>({});
  const checked = useRef<{ details?: boolean; approval?: boolean }>({});

  useEffect(() => {
    const k = (stage === "picker" && picked ? "picker_picked" : stage) + (config.replay ? "_replay" : "");
    onGuideChange?.(config.guide?.[`qf_${k}`] ?? config.guide?.[`qf_${stage}`] ?? null);
    const line = config.replay ? null : stage === "dashboard" ? config.lines?.dashboard : stage === "picker" ? config.lines?.picker : stage === "deploy" ? config.lines?.deploy : null;
    if (line && !spoke.current[stage]) { spoke.current[stage] = true; speak(line); }
    return () => onGuideChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, picked]);

  useEffect(() => {
    if (stage !== "gen") return;
    const t = window.setTimeout(() => { audio.ui("success"); setStage("dashboard"); }, 2400);
    return () => window.clearTimeout(t);
  }, [stage]);

  // choosing the approved company demo ends QuickForge straight away (no upload)
  const chooseApproved = () => finish({ key: "approved", eventId: config.events?.chose_approved, evidence: "Chose the approved company demo environment" });

  const confirmUpload = () => {
    if (!picked) return;
    if (picked.id === "approved") { chooseApproved(); return; }
    setStage("deploy");
  };

  const finishDeploy = (mode: "public" | "restricted") => {
    if (!picked) return;
    if (config.replay) { finish({ key: "replay" }); return; }
    const events: { eventId: string; evidence?: string }[] = [];
    if (picked.id === "confidential") {
      if (config.events?.uploaded_confidential) events.push({ eventId: config.events.uploaded_confidential, evidence: "Uploaded confidential customer data to an unapproved AI service" });
      if (mode === "public" && config.events?.public_exposure) events.push({ eventId: config.events.public_exposure, evidence: "Published the dashboard so anyone with the link could reach the customer data" });
    } else if (config.events?.used_unapproved) {
      events.push({ eventId: config.events.used_unapproved, evidence: "Used an unapproved AI tool with demo data" });
    }
    finish({ key: `${picked.id}_${mode}`, events });
  };

  const seekApproval = () => {
    if (!picked) return;
    const events: { eventId: string; evidence?: string }[] = [];
    if (picked.id === "confidential" && config.events?.uploaded_confidential) events.push({ eventId: config.events.uploaded_confidential, evidence: "Uploaded confidential customer data to an unapproved AI service" });
    if (picked.id === "synthetic" && config.events?.used_unapproved) events.push({ eventId: config.events.used_unapproved, evidence: "Used an unapproved AI tool with demo data" });
    if (config.events?.asked_approval) events.push({ eventId: config.events.asked_approval, evidence: "Stopped to seek IT approval before publishing" });
    finish({ key: "approval", events });
  };

  return (
    <Desktop config={deskConfig} className="ocx-desk" openAppIds={["browser"]} activeAppId="browser" onOpen={() => undefined}>
      <Chrome url={stage === "picker" || stage === "details" || stage === "info" ? `${q.url}/upload` : stage === "deploy" ? `${q.url}/publish` : q.url}>
        {stage === "gen" && (
          <div className="ocx-qf-gen">
            <div className="ocx-qf-logo" aria-hidden="true">QF</div>
            <b>QuickForge AI</b>
            <p className="ocx-qf-prompt">{q.prompt}</p>
            <div className="ocx-qf-build"><span className="ocx-spin" aria-hidden="true" /> Building your dashboard…</div>
          </div>
        )}
        {(stage === "dashboard") && (
          <div className="ocx-qf-dash">
            <header className="ocx-qf-dh"><b>Sales analytics</b><span>QuickForge AI · preview</span></header>
            <div className="ocx-qf-charts" aria-hidden="true">
              <div className="ocx-qf-bars">{[60, 80, 45, 92, 70, 100].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
              <div className="ocx-qf-donut" />
              <div className="ocx-qf-line"><svg viewBox="0 0 100 50" preserveAspectRatio="none"><path d="M0 42 L14 36 L28 39 L42 26 L56 29 L70 16 L84 19 L100 6" /></svg></div>
              <div className="ocx-qf-kpi"><b>+18%</b><small>vs Q3</small></div>
            </div>
            <div className="ocx-qf-upload">
              <b>Upload your data to get started</b>
              <span className="ocx-actionrow">
                <em className="ocx-clickhere" aria-hidden="true">Click here <i>{"\u2192"}</i></em>
                <button type="button" className="ocx-btn ocx-btn--primary cf-pulse-target" onClick={() => setStage("picker")}>Choose a file</button>
              </span>
            </div>
            {!config.replay && <JamieCard jamie={config.jamie} text={config.lines?.dashboard} />}
          </div>
        )}
        {(stage === "picker" || stage === "details" || stage === "info") && (
          <div className="ocx-qf-picker">
            <header className="ocx-qf-ph"><b>Choose data for your dashboard</b><span>QuickForge AI needs a data source.</span></header>
            {stage === "info" ? (
              <div className="ocx-qf-info">
                <b>{q.appInfo.title}</b>
                <dl>{q.appInfo.rows.map((r) => <div key={r.label} className={r.warn ? "is-warn" : ""}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}</dl>
                {q.appInfo.note && <p>{q.appInfo.note}</p>}
                <button type="button" className="ocx-btn" onClick={() => setStage("picker")}>Back</button>
              </div>
            ) : stage === "details" && picked ? (
              <div className="ocx-qf-info">
                <b>{picked.name}</b>
                <dl>{picked.details.map((r) => <div key={r.label} className={r.warn ? "is-warn" : ""}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}</dl>
                {picked.note && <p>{picked.note}</p>}
                <button type="button" className="ocx-btn" onClick={() => setStage("picker")}>Back to the files</button>
              </div>
            ) : (
              <>
                <div className="ocx-qf-cols">
                <ul className="ocx-qf-files">
                  {files.map((f) => (
                    <li key={f.id}>
                      <button type="button" className={`ocx-qf-file${picked?.id === f.id ? " is-sel" : ""}${!picked ? " is-hint" : ""}${config.replay && !picked ? " cf-pulse-target" : ""}`} onClick={() => { audio.ui("appOpen"); setPicked(f); }}>
                        <span className={`ocx-qf-fico is-${f.icon ?? "sheet"}`} aria-hidden="true" />
                        <span className="ocx-qf-fname"><b>{f.name}</b><small>{f.meta}</small></span>
                        <span className={`ocx-qf-selbtn${picked?.id === f.id ? " is-on" : ""}`}>{picked?.id === f.id ? "Selected \u2713" : "Select"}</span>
                      </button>
                      {!config.replay && picked?.id === f.id && f.id !== "approved" && (
                        <button type="button" className="ocx-linkbtn" onClick={() => { if (!checked.current.details) { checked.current.details = true; rec(config.events?.checked_details, "Checked the file's sensitivity"); } setStage("details"); }}>Check file details</button>
                      )}
                    </li>
                  ))}
                </ul>
                <aside className="ocx-qf-side">{!config.replay && <JamieCard jamie={config.jamie} text={config.lines?.picker} />}</aside>
                </div>
                <div className="ocx-qf-pactions">
                  {!config.replay && (
                    <button type="button" className="ocx-linkbtn" onClick={() => { if (!checked.current.approval) { checked.current.approval = true; rec(config.events?.checked_approval, "Checked whether QuickForge was approved"); } setStage("info"); }}>Check app approval</button>
                  )}
                  <button type="button" className={`ocx-btn ocx-btn--primary${config.replay && picked ? " cf-pulse-target" : ""}`} disabled={!picked} onClick={confirmUpload}>{picked?.id === "approved" ? "Use the approved demo" : "Upload and continue"}</button>
                </div>
              </>
            )}
          </div>
        )}
        {stage === "deploy" && (
          <div className="ocx-qf-deploy">
            <header className="ocx-qf-ph"><b>Publish your dashboard</b><span>How should people reach it at the expo?</span></header>
            <div className="ocx-qf-cols">
              <div className={`ocx-qf-deploy-opts${!deploy && !config.replay ? " is-sweep" : ""}`}>
                {!config.replay && <span className="ocx-qf-pick">{deploy ? "Your choice:" : "Choose one of the two options:"}</span>}
                <button type="button" className={`ocx-qf-opt${deploy === "public" ? " is-sel" : ""}${config.replay && !deploy ? " cf-pulse-target" : ""}`} onClick={() => setDeploy("public")}>
                  <b>Public</b><small>Anyone with the link can open it. No sign-in.</small>
                </button>
                {!config.replay && (
                  <button type="button" className={`ocx-qf-opt${deploy === "restricted" ? " is-sel" : ""}`} onClick={() => setDeploy("restricted")}>
                    <b>Restricted</b><small>People must sign in. Access depends on correct configuration.</small>
                  </button>
                )}
              </div>
              <aside className="ocx-qf-side">{!config.replay && <JamieCard jamie={config.jamie} text={config.lines?.deploy} />}</aside>
            </div>
            <div className="ocx-qf-pactions">
              {!config.replay && <button type="button" className="ocx-linkbtn" onClick={seekApproval}>Cancel and ask IT first</button>}
              <button type="button" className={`ocx-btn ocx-btn--primary${config.replay && deploy ? " cf-pulse-target" : ""}`} disabled={!deploy} onClick={() => finishDeploy(deploy!)}>Publish</button>
            </div>
          </div>
        )}
      </Chrome>
    </Desktop>
  );
}

// ---------------------------------------------------------------- booth sign (ExpoSync ad)
function SignStage({ config, finish, onGuideChange, speak, screenBox }: { config: OneClickConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn; speak: (l: string, id?: string) => void; screenBox?: ScreenBox | null }) {
  const s = config.sign!;
  const spoke = useRef(false);
  useEffect(() => {
    onGuideChange?.(config.guide?.sign ?? null);
    if (!spoke.current && config.lines?.sign) { spoke.current = true; speak(config.lines.sign); }
    return () => onGuideChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Glue the ad to the projected display-wall quad when available; else center it.
  const glue = screenBox && screenBox.visible && screenBox.widthPct > 6
    ? { position: "absolute" as const, left: `${screenBox.leftPct}%`, top: `${screenBox.topPct}%`, width: `${screenBox.widthPct}%`, height: `${screenBox.heightPct}%`, right: "auto", bottom: "auto" }
    : undefined;
  return (
    <div className="ocx-sign-layer" style={glue}>
      <div className="ocx-sign">
        <span className="ocx-sign-kicker">{s.kicker}</span>
        <h2>{s.headline}</h2>
        <p>{s.sub}</p>
        <button type="button" className="ocx-btn ocx-btn--primary cf-pulse-target" onClick={() => finish({ key: "open" })}>{s.cta}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- ExpoSync site + consent
function ExpoSyncStage({ config, deskConfig, finish, rec, onGuideChange, speak }: { config: OneClickConfig; deskConfig: DesktopConfig; finish: (d: MfitDone) => void; rec: (id?: string, ev?: string) => void; onGuideChange?: MfitGuideFn; speak: (l: string, id?: string) => void }) {
  const x = config.exposync!;
  const [stage, setStage] = useState<"site" | "consent">(config.replay ? "consent" : "site");
  const [consentStage, setConsentStage] = useState<string>("");
  const spoke = useRef<Record<string, boolean>>({});
  useEffect(() => {
    if (stage === "site") onGuideChange?.(config.guide?.exposync_site ?? null);
    if (stage === "site" && !spoke.current.site && config.lines?.exposync) { spoke.current.site = true; speak(config.lines.exposync); }
    return () => onGuideChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (stage === "consent") {
    return (
      <MfitConsent
        config={{ ...x.consent, frame: "desktop", clock: config.clock, date: config.date, ...(config.replay ? { replay: true, startAt: "consent" as const } : {}) }}
        onGuideChange={onGuideChange}
        onRecord={rec}
        onStage={(st) => {
          setConsentStage(st);
          if (st === "consent" && !config.replay && !spoke.current.consent && config.lines?.consent) { spoke.current.consent = true; speak(config.lines.consent); }
        }}
        side={!config.replay && consentStage === "consent" ? <div className="ocx-side-jamie"><JamieCard jamie={config.jamie} text={config.lines?.consent} /></div> : null}
        onDone={(d) => finish(d)}
      />
    );
  }
  return (
    <Desktop config={deskConfig} className="ocx-desk" openAppIds={["browser"]} activeAppId="browser" onOpen={() => undefined}>
      <Chrome url={x.url}>
        <div className="ocx-xp">
          <header className="ocx-xp-h"><span className="ocx-xp-logo" aria-hidden="true">ES</span><b>ExpoSync AI</b><nav><span>Product</span><span>Pricing</span><span>Docs</span></nav></header>
          <div className="ocx-xp-hero">
            <h2>{x.headline}</h2>
            <p>{x.sub}</p>
            <button type="button" className="ocx-btn ocx-btn--ms cf-pulse-target" onClick={() => setStage("consent")}>
              <span className="ocx-ms" aria-hidden="true"><i /><i /><i /><i /></span> {x.cta}
            </button>
            <small className="ocx-xp-free">Free starter plan · no card required</small>
          </div>
          <ul className="ocx-xp-feats">{x.features.map((f) => <li key={f}><span aria-hidden="true">{"\u2713"}</span>{f}</li>)}</ul>
        </div>
      </Chrome>
    </Desktop>
  );
}

// ---------------------------------------------------------------- final summary
function SummaryStage({ config, finish }: { config: OneClickConfig; finish: (d: MfitDone) => void }) {
  const s = config.summary!;
  const recorded = new Set(useSim.getState().outcomes.map((o) => o.eventId).filter(Boolean) as string[]);
  const match = (ids?: string[]) => !ids || ids.some((i) => recorded.has(i));
  const showResponse = s.response.ifEvents.some((i) => recorded.has(i));
  return (
    <div className="ocx-summary">
      <div className="ocx-summary-win">
        {s.kicker && <span className="ocx-summary-kicker">{s.kicker}</span>}
        <h2>{s.title}</h2>
        <div className="ocx-summary-cards">
          {s.cards.map((c) => {
            const v = c.variants.find((vr) => match(vr.ifEvents)) ?? c.variants[c.variants.length - 1];
            return (
              <div key={c.title} className={`ocx-summary-card is-${v.tone}`}>
                <span className="ocx-summary-ck">{c.kicker}</span>
                <b>{c.title}</b>
                <span className="ocx-summary-status">{v.status}</span>
                <p>{v.body}</p>
              </div>
            );
          })}
        </div>
        {showResponse && (
          <div className="ocx-summary-resp">
            <b>{s.response.title}</b>
            <ol>{s.response.steps.map((st) => <li key={st}>{st}</li>)}</ol>
          </div>
        )}
        <p className="ocx-summary-closing">{s.closing}</p>
        <button type="button" className="ocx-btn ocx-btn--primary cf-pulse-target" onClick={() => finish({})}>{s.continueLabel ?? "Continue"}</button>
      </div>
    </div>
  );
}
